/**
 * User Preferences Context
 * Provides formatting utilities based on user's locale preferences
 * Resolves tenant_default values using tenant settings
 */

import React, { createContext, useContext, useMemo } from 'react';
import { parseISO } from 'date-fns';
import { useAuth } from '../auth';
import { useTenantLocaleSettings } from '../hooks/useTenantSettings';
import type { NumberFormat, DateFormat, TimeFormat, SupportedLanguage, WeekStartDay } from '../auth/types';
import localeConfig from '../../shared-config/locale.config.json';
import { resolveTimezonePreference } from '../utils/timezone';
import { formatDateInZone, type DateStyle } from '../utils/dateFormat';

// Language to locale mapping
const LANGUAGE_LOCALES: Record<SupportedLanguage, string> = {
  en: 'en-US',
  es: 'es-ES',
  he: 'he-IL',
};

// Get language defaults from locale config
function getLanguageDefaultNumberFormat(language: SupportedLanguage): 'comma_dot' | 'dot_comma' {
  const langConfig = localeConfig.languages.values[language as keyof typeof localeConfig.languages.values];
  return (langConfig?.defaultNumberFormat as 'comma_dot' | 'dot_comma') || 'comma_dot';
}

function getLanguageDefaultDateFormat(language: SupportedLanguage): 'dd/mm/yyyy' | 'mm/dd/yyyy' | 'yyyy-mm-dd' {
  const langConfig = localeConfig.languages.values[language as keyof typeof localeConfig.languages.values];
  return (langConfig?.defaultDateFormat as 'dd/mm/yyyy' | 'mm/dd/yyyy' | 'yyyy-mm-dd') || 'mm/dd/yyyy';
}

export interface FormatOptions {
  locale: string;
  numberFormat: 'comma_dot' | 'dot_comma';
  dateFormat: 'dd/mm/yyyy' | 'mm/dd/yyyy' | 'yyyy-mm-dd';
  // Resolved to a concrete clock — the 'tenant_default' sentinel is never present here.
  timeFormat: '12h' | '24h';
  timezone: string;
}

export interface UserPreferencesContextType {
  // Raw preferences
  preferredLanguage: SupportedLanguage;
  numberFormat: NumberFormat;
  dateFormat: DateFormat;
  timeFormat: TimeFormat;
  timezone: string;
  /** Raw week-start preference (locale_default = auto). */
  weekStartDay: WeekStartDay;
  /** First day of week in date-fns convention: 0 = Sunday, 1 = Monday. */
  weekStartsOn: 0 | 1;

  // Resolved options (auto replaced with actual values)
  formatOptions: FormatOptions;

  // Formatting functions
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string;
  formatCurrency: (value: number, currency: string, options?: Intl.NumberFormatOptions) => string;
  /** For INSTANTS (TIMESTAMPTZ) — rendered in the user's configured timezone. */
  formatDate: (date: Date | string, format?: DateStyle) => string;
  /**
   * For DATE-ONLY values (DATE columns: planning dates, due dates). Rendered in the
   * ambient frame they were parsed/written in, so the day shown always equals the
   * day stored, whatever timezone the user has configured.
   */
  formatPlanningDate: (date: Date | string, format?: DateStyle) => string;
  formatDateTime: (date: Date | string, format?: DateStyle) => string;
  formatTime: (date: Date | string) => string;

  // Parsing functions (for inputs)
  parseNumber: (value: string) => number | null;

  // Display format strings for UI
  getNumberFormatExample: () => string;
  getDateFormatPattern: () => string;
}

const UserPreferencesContext = createContext<UserPreferencesContextType | undefined>(undefined);

export function UserPreferencesProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { data: tenantSettings } = useTenantLocaleSettings();

  // Get preferences with defaults
  const preferredLanguage: SupportedLanguage = user?.preferred_language ?? 'en';
  const numberFormat: NumberFormat = user?.number_format ?? 'tenant_default';
  const dateFormat: DateFormat = user?.date_format ?? 'tenant_default';
  const timeFormat: TimeFormat = user?.time_format ?? 'tenant_default';
  // An explicit choice wins ('UTC' included); only the 'browser_default' sentinel
  // defers to the browser's ambient zone.
  const timezone: string = resolveTimezonePreference(user?.timezone);

  // First day of week. The backend resolves the 'locale_default' sentinel from the
  // user's country/language into a concrete value; default to Monday (ISO 8601).
  const weekStartDay: WeekStartDay = user?.week_start_day ?? 'locale_default';
  const weekStartsOn: 0 | 1 = user?.resolved_week_start_day === 'sunday' ? 0 : 1;

  // Get tenant defaults (fallback to language defaults if tenant setting is 'language_default')
  const tenantDefaultLanguage: SupportedLanguage = tenantSettings?.default_language ?? 'en';

  const tenantNumberFormat = tenantSettings?.default_number_format === 'language_default' || !tenantSettings?.default_number_format
    ? getLanguageDefaultNumberFormat(tenantDefaultLanguage)
    : tenantSettings.default_number_format as 'comma_dot' | 'dot_comma';

  const tenantDateFormat = tenantSettings?.default_date_format === 'language_default' || !tenantSettings?.default_date_format
    ? getLanguageDefaultDateFormat(tenantDefaultLanguage)
    : tenantSettings.default_date_format as 'dd/mm/yyyy' | 'mm/dd/yyyy' | 'yyyy-mm-dd';

  // The tenant clock default is a concrete '12h'/'24h' (no language sentinel);
  // fall back to '24h' (international norm) when the tenant has none.
  const tenantTimeFormat: '12h' | '24h' = tenantSettings?.default_time_format ?? '24h';

  // Resolve "tenant_default" formats using tenant settings
  const resolvedNumberFormat = numberFormat === 'tenant_default'
    ? tenantNumberFormat
    : numberFormat;

  const resolvedDateFormat = dateFormat === 'tenant_default'
    ? tenantDateFormat
    : dateFormat;

  const resolvedTimeFormat: '12h' | '24h' = timeFormat === 'tenant_default'
    ? tenantTimeFormat
    : timeFormat;

  const locale = LANGUAGE_LOCALES[preferredLanguage];

  const formatOptions: FormatOptions = useMemo(() => ({
    locale,
    numberFormat: resolvedNumberFormat,
    dateFormat: resolvedDateFormat,
    timeFormat: resolvedTimeFormat,
    timezone,
  }), [locale, resolvedNumberFormat, resolvedDateFormat, resolvedTimeFormat, timezone]);

  // Create number formatter based on format preference
  const createNumberLocale = useMemo(() => {
    // Use locales that match the desired format
    return resolvedNumberFormat === 'comma_dot' ? 'en-US' : 'de-DE';
  }, [resolvedNumberFormat]);

  const formatNumber = useMemo(() => {
    return (value: number, options?: Intl.NumberFormatOptions) => {
      return new Intl.NumberFormat(createNumberLocale, options).format(value);
    };
  }, [createNumberLocale]);

  const formatCurrency = useMemo(() => {
    return (value: number, currency: string, options?: Intl.NumberFormatOptions) => {
      return new Intl.NumberFormat(createNumberLocale, {
        style: 'currency',
        currency,
        ...options,
      }).format(value);
    };
  }, [createNumberLocale]);

  // An INSTANT (TIMESTAMPTZ): render the wall-clock day of the user's CONFIGURED
  // zone. The `Date` getters would answer for the browser's ambient zone and drift
  // a day for far-from-UTC users.
  const formatDate = useMemo(() => {
    return (date: Date | string, format: DateStyle = 'medium') => {
      const d = typeof date === 'string' ? new Date(date) : date;
      return formatDateInZone(d, format, resolvedDateFormat, timezone);
    };
  }, [resolvedDateFormat, timezone]);

  // A DATE-ONLY value (a `DATE` column: planning start/end, due date). It carries no
  // instant — it was parsed to local midnight and is written back from the local
  // frame — so it must be read back in that same ambient frame. Passing the
  // configured zone here is what made the roadmap's labels disagree by a day with
  // the dates it had just persisted.
  const formatPlanningDate = useMemo(() => {
    return (date: Date | string, format: DateStyle = 'medium') => {
      const d = typeof date === 'string' ? parseISO(date) : date;
      return formatDateInZone(d, format, resolvedDateFormat, undefined);
    };
  }, [resolvedDateFormat]);

  const formatTime = useMemo(() => {
    return (date: Date | string) => {
      const d = typeof date === 'string' ? new Date(date) : date;
      if (isNaN(d.getTime())) return '';

      return new Intl.DateTimeFormat(locale, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: resolvedTimeFormat === '12h',
        timeZone: timezone,
      }).format(d);
    };
  }, [locale, resolvedTimeFormat, timezone]);

  const formatDateTime = useMemo(() => {
    return (date: Date | string, format: DateStyle = 'medium') => {
      const dateStr = formatDate(date, format);
      const timeStr = formatTime(date);
      return `${dateStr} ${timeStr}`;
    };
  }, [formatDate, formatTime]);

  const parseNumber = useMemo(() => {
    return (value: string): number | null => {
      if (!value || value.trim() === '') return null;

      let normalized = value.trim();

      if (resolvedNumberFormat === 'dot_comma') {
        // 123.456,78 -> 123456.78
        normalized = normalized.replace(/\./g, '').replace(',', '.');
      } else {
        // 123,456.78 -> 123456.78
        normalized = normalized.replace(/,/g, '');
      }

      const parsed = parseFloat(normalized);
      return isNaN(parsed) ? null : parsed;
    };
  }, [resolvedNumberFormat]);

  const getNumberFormatExample = useMemo(() => {
    return () => {
      return resolvedNumberFormat === 'comma_dot' ? '123,456.78' : '123.456,78';
    };
  }, [resolvedNumberFormat]);

  const getDateFormatPattern = useMemo(() => {
    return () => resolvedDateFormat;
  }, [resolvedDateFormat]);

  const value: UserPreferencesContextType = useMemo(() => ({
    preferredLanguage,
    numberFormat,
    dateFormat,
    timeFormat,
    timezone,
    weekStartDay,
    weekStartsOn,
    formatOptions,
    formatNumber,
    formatCurrency,
    formatDate,
    formatPlanningDate,
    formatDateTime,
    formatTime,
    parseNumber,
    getNumberFormatExample,
    getDateFormatPattern,
  }), [
    preferredLanguage,
    numberFormat,
    dateFormat,
    timeFormat,
    timezone,
    weekStartDay,
    weekStartsOn,
    formatOptions,
    formatNumber,
    formatCurrency,
    formatDate,
    formatPlanningDate,
    formatDateTime,
    formatTime,
    parseNumber,
    getNumberFormatExample,
    getDateFormatPattern,
  ]);

  return (
    <UserPreferencesContext.Provider value={value}>
      {children}
    </UserPreferencesContext.Provider>
  );
}

export function useUserPreferences(): UserPreferencesContextType {
  const context = useContext(UserPreferencesContext);
  if (context === undefined) {
    throw new Error('useUserPreferences must be used within a UserPreferencesProvider');
  }
  return context;
}
